import { useState, useEffect, useMemo } from "react";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { useAccidentalSubmitGuard } from "../hooks/useAccidentalSubmitGuard";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { Button } from "./ui/button";
import { ReassuranceLine } from "./ui/ReassuranceLine";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import { getStorefrontConfig, getStorefrontUrl } from "../lib/storefront-config";
import { createShopifyAccount, updateShopifyCustomer } from "../lib/shopify-customer-creation";
import { identifyOnAccountCreation } from "../lib/klaviyo-subscribe";
import { updateShopifyHeader } from "../lib/shopify-header-utils";
import { normalizeInput } from "../lib/normalize-input";
import { focusFirstInvalidField } from "../lib/focus-first-invalid";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  isReservedNanp5550100Through0199,
} from "../lib/nanp-phone-us-state-check";
import { isPuertoRico } from "../lib/puerto-rico-check";
import type { AccountData } from "./AccountScreen";
import type { ProgressSectionId } from "../lib/progress-calculator";

// Single source of truth lives in lib/progress-calculator.ts; re-exported here so existing
// `import type { AccountDetailsStep } from "./AccountRegister"` call sites don't change.
import type { AccountDetailsStep } from "../lib/progress-calculator";
export type { AccountDetailsStep } from "../lib/progress-calculator";

type RegisterFormState = {
  email: string;
  firstName: string;
  lastName: string;
  dobMonth: string;
  dobDay: string;
  dobYear: string;
  sex: string;
  phone: string;
  tnc: boolean;
  sms_marketing_accept: boolean;
};

const formatPhoneForDisplay = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const buildRegisterFormFromAccountData = (
  email: string,
  accountData?: AccountData | null
): RegisterFormState => {
  const empty: RegisterFormState = {
    email,
    firstName: "",
    lastName: "",
    dobMonth: "",
    dobDay: "",
    dobYear: "",
    sex: "",
    phone: "",
    tnc: false,
    sms_marketing_accept: false,
  };

  if (!accountData) return empty;

  let dobMonth = "";
  let dobDay = "";
  let dobYear = "";
  if (accountData.dob) {
    const [year, month, day] = accountData.dob.split("-");
    dobYear = year || "";
    dobMonth = month ? String(parseInt(month, 10)) : "";
    dobDay = day ? String(parseInt(day, 10)) : "";
  }

  const smsConsent =
    accountData.sms_marketing_accept === true ||
    accountData.sms_marketing_accept === "true";

  return {
    email: accountData.email || email,
    firstName: accountData.firstName || "",
    lastName: accountData.lastName || "",
    dobMonth,
    dobDay,
    dobYear,
    sex: accountData.sex || "",
    phone: accountData.phone ? formatPhoneForDisplay(accountData.phone) : "",
    tnc: accountData.tnc === true,
    sms_marketing_accept: smsConsent,
  };
};

declare global {
  interface Window {
    _learnq: any[];
  }
}

/** US national 10-digit number — matches what Storefront `customerCreate` usually accepts as E.164 +1… */
function isValidUsNationalPhoneDigits(digits10: string): boolean {
  if (!/^\d{10}$/.test(digits10)) return false;
  const pn = parsePhoneNumberFromString(digits10, "US");
  return pn?.isValid() === true;
}

function isPhoneAndStateComplete(
  formattedPhone: string,
  selectedState: string,
  puertoRicoSelected: boolean
): boolean {
  if (puertoRicoSelected || selectedState.trim() === "") return false;
  const pd = formattedPhone.replace(/\D/g, "");
  if (pd.length !== 10 || !isValidUsNationalPhoneDigits(pd)) return false;
  if (isReservedNanp5550100Through0199(pd)) return false;
  return true;
}

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  isValid: boolean;
}

function validatePassword(password: string): PasswordValidation {
  const minLength = password.length >= 5;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  return {
    minLength,
    hasUppercase,
    hasNumber,
    hasSpecialChar,
    isValid: minLength && hasUppercase && hasNumber && hasSpecialChar,
  };
}

function PasswordChecklist({ password, language }: { password: string; language: "en" | "es" }) {
  const validation = validatePassword(password);

  const requirements = [
    { met: validation.minLength, label: language === "es" ? "Mínimo 5 caracteres" : "At least 5 characters" },
    { met: validation.hasUppercase, label: language === "es" ? "Una letra mayúscula" : "One uppercase letter" },
    { met: validation.hasNumber, label: language === "es" ? "Un número" : "One number" },
    {
      met: validation.hasSpecialChar,
      label: language === "es" ? "Un carácter especial (!@#$%^&*)" : "One special character (!@#$%^&*)",
    },
  ];

  return (
    <ul className="mt-2 space-y-1 text-sm">
      {requirements.map((req, index) => (
        <li key={index} className="flex items-center gap-2">
          {req.met ? (
            <Check size={16} className="text-green-600 flex-shrink-0" />
          ) : (
            <X size={16} className="text-gray-400 flex-shrink-0" />
          )}
          <span className={req.met ? "text-green-600" : "text-gray-500"}>{req.label}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Same silent-login pattern used across the flow (AccountLogin.tsx) — loads the multipass URL in
 * a hidden iframe to set a real Shopify storefront session cookie, best-effort only.
 */
async function silentlyLoginViaMultipass(token: string): Promise<void> {
  try {
    const multipassResponse = await fetch("/apps/rx/generate-multipass", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerAccessToken: token }),
    });
    const multipassData = await multipassResponse.json();
    const redirectUrl = multipassData.multipass_url || multipassData.redirect;
    if (!redirectUrl) return;

    await new Promise<void>((resolve) => {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      let hasCompleted = false;
      const finish = async () => {
        if (hasCompleted) return;
        hasCompleted = true;
        await updateShopifyHeader();
        document.body.removeChild(iframe);
        resolve();
      };

      const fallbackTimeout = setTimeout(finish, 5000);
      iframe.onload = async () => {
        await new Promise((r) => setTimeout(r, 800));
        clearTimeout(fallbackTimeout);
        await finish();
      };
      iframe.onerror = () => {
        clearTimeout(fallbackTimeout);
        finish();
      };

      let iframeSrc = redirectUrl;
      if (iframeSrc.includes("srx-meds.myshopify.com")) {
        iframeSrc = iframeSrc.replace("srx-meds.myshopify.com", "srreceta.com");
      }
      iframe.src = iframeSrc;
    });
  } catch (err) {
    // Multipass is best-effort — a missing storefront session cookie doesn't block progress.
    console.error(err);
  }
}

/** Best-effort: MDI /apps/rx/new-customer error body may reference phone / area code. */
function mdiErrorLooksPhoneRelated(data: any): { match: boolean; detail: string } {
  const detail =
    (typeof data?.message === "string" && data.message) ||
    (typeof data?.error === "string" && data.error) ||
    "";
  const blob = `${detail} ${JSON.stringify(data ?? {})}`.toLowerCase();
  const match = /phone|telephone|area code|area_code|tel[eé]fono|n[uú]mero/.test(blob);
  return { match, detail };
}

interface AccountRegisterProps {
  onContinue: (data?: any) => void;
  onBack: () => void;
  onClose?: () => void;
  accountDetailsStep: AccountDetailsStep;
  onAccountDetailsStepChange: (step: AccountDetailsStep) => void;
  selectedState: string;
  language?: "en" | "es";
  logoUrl?: string;
  email: string;
  initialAccountData?: AccountData | null;
  customerId?: string | null;
  shopifyCustomerId?: string | null; // Shopify customer ID from login
  customerAccessToken?: string | null; // Customer access token from login
  currentStep: number;
  totalSteps: number;
  sectionIndex: number;
  totalSections: number;
  sectionId: ProgressSectionId;
}

export function AccountRegister({
  onContinue,
  onBack,
  onClose,
  accountDetailsStep,
  onAccountDetailsStepChange,
  selectedState,
  language = "en",
  logoUrl,
  email,
  initialAccountData,
  customerId,
  shopifyCustomerId: propShopifyCustomerId,
  customerAccessToken: propCustomerAccessToken,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
}: AccountRegisterProps) {
  const t = getNamespaceTranslations(language, "account")!;
  const tShipping = getNamespaceTranslations(language, "patientShippingInfo" as any);

  const [form, setForm] = useState(() =>
    buildRegisterFormFromAccountData(email, initialAccountData)
  );

  useEffect(() => {
    if (initialAccountData) {
      setForm(buildRegisterFormFromAccountData(email, initialAccountData));
    }
  }, [email, initialAccountData]);

  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false as boolean);
  const [isDobInvalid, setIsDobInvalid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [dobTouched, setDobTouched] = useState(false);
  const [sexTouched, setSexTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [showConfirmPasswordText, setShowConfirmPasswordText] = useState(false);
  const customerAccessToken = propCustomerAccessToken || initialAccountData?.customerAccessToken || null;
  const shopifyCustomerId = propShopifyCustomerId || initialAccountData?.shopify_customer_id || null;
  const isPR = isPuertoRico(selectedState);
  const { shouldSuppressSubmit, armGuard } = useAccidentalSubmitGuard(accountDetailsStep === "access");

  // SRX-198: password now lives on this screen, alongside phone. Only a brand-new registrant
  // (no Shopify account yet) needs to set one — a customer who arrived via AccountLogin already
  // has a token and skips these fields entirely, same conditional pattern as the old T&C fallback.
  const needsPassword = !customerAccessToken;
  const passwordValidation = validatePassword(password);
  const showPasswordRequiredError = passwordTouched && password.length === 0;
  const showConfirmPasswordError =
    confirmPasswordTouched && (confirmPassword.length === 0 || password !== confirmPassword);
  const confirmPasswordErrorMessage =
    confirmPassword.length === 0 ? t.fieldRequired : t.passwordMismatchError || "Passwords do not match";

  const phoneDigits = form.phone.replace(/\D/g, "");

  const phoneIssueKey = useMemo(() => {
    const d = phoneDigits;
    if (d.length !== 10) {
      return d.length > 0 ? ("incomplete" as const) : null;
    }
    if (!isValidUsNationalPhoneDigits(d)) return "invalid" as const;
    if (isReservedNanp5550100Through0199(d)) return "testLine" as const;
    return null;
  }, [phoneDigits, selectedState]);

  const showPhoneUnderFieldError =
    (phoneTouched && phoneDigits.length === 0) ||
    (phoneTouched && phoneIssueKey === "incomplete") ||
    (phoneIssueKey !== null && phoneIssueKey !== "incomplete");

  const phoneUnderFieldMessage =
    phoneTouched && phoneDigits.length === 0
      ? t.fieldRequired
      : phoneIssueKey === null || !showPhoneUnderFieldError
        ? ""
        : phoneIssueKey === "incomplete" || phoneIssueKey === "invalid"
          ? t.invalidPhoneError
          : phoneIssueKey === "testLine"
            ? t.phoneTestLineError
            : phoneIssueKey === "tollFree"
              ? t.phoneTollFreeError
              : phoneIssueKey === "territory"
                ? t.phoneTerritoryError
                : t.phoneStateMismatchError;

  const isPhoneFieldInvalidForStyle =
    (phoneTouched && phoneDigits.length === 0) ||
    (phoneTouched && phoneDigits.length > 0 && phoneDigits.length < 10) ||
    (phoneIssueKey !== null && phoneIssueKey !== "incomplete");

  const firstNameError =
    firstNameTouched && form.firstName.trim().length === 0
      ? t.fieldRequired
      : firstNameTouched && form.firstName.trim().length > 0 && form.firstName.trim().length < 2
        ? t.firstNameTooShortError
        : "";

  const lastNameError =
    lastNameTouched && form.lastName.trim().length === 0
      ? t.fieldRequired
      : lastNameTouched && form.lastName.trim().length > 0 && form.lastName.trim().length < 2
        ? t.lastNameTooShortError
        : "";

  const isDobIncomplete =
    form.dobMonth.trim() === "" || form.dobDay.trim() === "" || form.dobYear.trim().length !== 4;
  const showDobRequiredError = dobTouched && isDobIncomplete && !isDobInvalid;

  const showSexRequiredError = sexTouched && form.sex === "";
  // Format phone number as US format: (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");

    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);

    // Format based on length
    if (limitedDigits.length === 0) return "";
    if (limitedDigits.length <= 3) return `(${limitedDigits}`;
    if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  };

  // Helper function to validate email has proper TLD (minimum 2 characters)
  const isValidEmailTLD = (emailAddress: string): boolean => {
    const tldMatch = emailAddress.match(/\.([a-zA-Z]+)$/);
    return tldMatch ? tldMatch[1].length >= 2 : false;
  };

  // Validate required fields dynamically
  useEffect(() => {
    // Calculate if DOB is valid (at least 18 years old and not in future)
    let isDobValid = false;
    // Only consider year complete when all 4 digits are entered
    const isYearComplete = form.dobYear.trim().length === 4;
    const hasAllDobFields = form.dobMonth.trim() !== "" && form.dobDay.trim() !== "" && isYearComplete;

    if (hasAllDobFields) {
      const month = parseInt(form.dobMonth, 10);
      const day = parseInt(form.dobDay, 10);
      const year = parseInt(form.dobYear, 10);

      // Basic validation for month, day, year ranges
      const isValidFormat = month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= new Date().getFullYear();

      if (isValidFormat) {
        // Create date (month is 0-indexed in JS)
        const dobDate = new Date(year, month - 1, day);
        const today = new Date();
        const eighteenYearsAgo = new Date(
          today.getFullYear() - 18,
          today.getMonth(),
          today.getDate()
        );

        // Verify the date is valid (e.g., not Feb 30)
        const isRealDate = dobDate.getMonth() === month - 1 && dobDate.getDate() === day;

        // DOB must not be in the future and must be at least 18 years ago
        isDobValid = isRealDate && dobDate <= today && dobDate <= eighteenYearsAgo;
      }

      // Set error state if DOB is filled but invalid
      setIsDobInvalid(!isDobValid);
    } else {
      setIsDobInvalid(false); // No error if year is incomplete
    }

    const phoneDigitsOk = isPhoneAndStateComplete(
      form.phone,
      selectedState,
      isPR
    );

    const passwordOk =
      !needsPassword || (passwordValidation.isValid && password === confirmPassword);

    const valid =
      !!selectedState &&                   // state was captured earlier in the flow
      !isPR &&                             // block Puerto Rico
      form.firstName.trim().length >= 2 && // minimum 2 characters for first name
      form.lastName.trim().length >= 2 && // minimum 2 characters for last name
      isValidEmailTLD(email) &&            // email TLD must be at least 2 characters
      isDobValid &&                        // valid DOB (18+ and not in future)
      form.sex !== "" &&
      phoneDigitsOk &&                     // 10 digits — matches PatientInfo phone schema
      form.tnc &&
      passwordOk;

    setIsValid(valid);
  }, [form, isPR, selectedState, email, needsPassword, password, confirmPassword, passwordValidation.isValid]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    const checked = type === "checkbox" && "checked" in e.target ? e.target.checked : undefined;

    // Clear error message when user makes changes
    if (errorMessage) {
      setErrorMessage("");
    }

    // Format phone number as user types
    const processedValue = name === "phone" ? formatPhoneNumber(value) : value;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));
  };

  /**
   * handleDobChange - Handles date of birth input with smart auto-formatting and field navigation
   *
   * This function provides a user-friendly DOB input experience by:
   * 1. Stripping non-numeric characters
   * 2. Auto-padding single digits that can only be one value (e.g., "5" -> "05" for day)
   * 3. Clamping values to valid ranges (month: 1-12, day: 1-31)
   * 4. Auto-focusing the next field when current field is complete
   *
   * MONTH LOGIC (1-12):
   * - Single digit "0": Wait for second digit (could be 01-09)
   * - Single digit "1": Wait for second digit (could be 10, 11, 12)
   * - Single digit 2-9: Auto-pad with "0" (e.g., "3" -> "03") and advance (en→day, es→year)
   * - Two digits > 12: Clamp to "12"
   * - Two digits "00": Convert to "01" (invalid month)
   *
   * DAY LOGIC (1-31):
   * - Single digit "0": Wait for second digit (could be 01-09)
   * - Single digit 1-3: Wait for second digit (could be 10-19, 20-29, 30-31)
   * - Single digit 4-9: Auto-pad with "0" (e.g., "5" -> "05") and advance (en→year, es→month)
   * - Two digits > 31: Clamp to "31"
   * - Two digits "00": Convert to "01" (invalid day)
   *
   * Field order is always Month→Day→Year (US format) regardless of language. Submitted value
   * remains YYYY-MM-DD.
   *
   * YEAR LOGIC:
   * - Simply accepts up to 4 numeric digits
   * - Validation (18+ years, not in future) happens in useEffect and on submit
   */
  const handleDobChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    // Strip all non-numeric characters
    const numericValue = value.replace(/\D/g, '');

    // Clear error message when user makes changes
    if (errorMessage) {
      setErrorMessage("");
    }

    let finalValue = numericValue;
    let shouldFocusNext = false;

    if (name === 'dobMonth') {
      // MONTH INPUT (valid range: 01-12)
      const num = parseInt(numericValue, 10);

      if (numericValue.length === 1 && num > 1) {
        // Single digit 2-9: Only valid as months 02-09, so auto-pad with "0"
        // Example: User types "3" -> becomes "03" (March)
        // Note: "0" and "1" need to wait (could become 01-09 or 10-12)
        finalValue = `0${numericValue}`;
        shouldFocusNext = true;
      } else if (numericValue.length === 2 && num > 12) {
        // Two digits exceeding 12: Clamp to maximum valid month
        // Example: User types "15" -> becomes "12"
        finalValue = '12';
        shouldFocusNext = true;
      } else if (numericValue.length === 2 && num === 0) {
        // Two digits "00": Invalid month, convert to January
        // Note: This is a safeguard - normally unreachable since typing "0" then "0"
        // would require the first "0" to persist (which it does, waiting for second digit)
        finalValue = '01';
        shouldFocusNext = true;
      } else if (numericValue.length === 2) {
        // Valid two-digit month (01-12): Move to next field
        shouldFocusNext = true;
      }
    } else if (name === 'dobDay') {
      // DAY INPUT (valid range: 01-31)
      const num = parseInt(numericValue, 10);

      if (numericValue.length === 1 && num > 3) {
        // Single digit 4-9: Only valid as days 04-09, so auto-pad with "0"
        // Example: User types "5" -> becomes "05"
        // Note: "0", "1", "2", "3" need to wait (could become 01-09, 10-19, 20-29, 30-31)
        finalValue = `0${numericValue}`;
        shouldFocusNext = true;
      } else if (numericValue.length === 2 && num > 31) {
        // Two digits exceeding 31: Clamp to maximum valid day
        // Example: User types "35" -> becomes "31"
        finalValue = '31';
        shouldFocusNext = true;
      } else if (numericValue.length === 2 && num === 0) {
        // Two digits "00": Invalid day, convert to 1st
        // Safeguard for edge case
        finalValue = '01';
        shouldFocusNext = true;
      } else if (numericValue.length === 2) {
        // Valid two-digit day (01-31): Move to next field
        shouldFocusNext = true;
      }
    } else if (name === 'dobYear') {
      // YEAR INPUT: Accept up to 4 digits, no auto-formatting
      // Full validation (18+ years old, not in future) happens elsewhere
      finalValue = numericValue.slice(0, 4);
    }

    setForm((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Auto-focus next field when current field is complete: MM → DD → YYYY (always, regardless of language)
    if (shouldFocusNext) {
      if (name === "dobMonth") {
        document.getElementById("dobDay")?.focus();
      } else if (name === "dobDay") {
        document.getElementById("dobYear")?.focus();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Registration API must only run from the final (access) screen (avoids Enter / stray submit on identity)
    if (accountDetailsStep !== "access") return;
    if (shouldSuppressSubmit()) return;
    if (!isValid) {
      // `isValid` also covers name/DOB/sex/email/state — none of which have a visible field on
      // this (access) screen. Without this branch, a regression in any of those silently blocks
      // submission: phone/tnc look fine, nothing is touched, and nothing is shown.
      if (!isStepValid("identity")) {
        setFirstNameTouched(true);
        setLastNameTouched(true);
        setDobTouched(true);
        setSexTouched(true);
        onAccountDetailsStepChange("identity");
        return;
      }

      const phoneOk = isPhoneAndStateComplete(form.phone, selectedState, isPR);
      const passwordOk =
        !needsPassword || (passwordValidation.isValid && password === confirmPassword);
      setPhoneTouched(true);
      if (needsPassword) {
        setPasswordTouched(true);
        setConfirmPasswordTouched(true);
      }
      if (!phoneOk || !form.tnc || !passwordOk) {
        focusFirstInvalidField([
          !phoneOk ? "phone" : null,
          needsPassword && !passwordOk ? "password" : null,
        ]);
        return;
      }

      // Only state/email can still be at fault here, and neither is editable on this screen.
      setErrorMessage(t.checkDetailsError);
      return;
    }

    // Build DOB string from separate fields (YYYY-MM-DD format)
    const month = parseInt(form.dobMonth, 10);
    const day = parseInt(form.dobDay, 10);
    const year = parseInt(form.dobYear, 10);
    const dobString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Additional validation before submission to prevent bad data
    const dobDate = new Date(year, month - 1, day);
    const today = new Date();
    const eighteenYearsAgo = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );

    // Validate DOB is at least 18 years old and not in future
    if (dobDate > today || dobDate > eighteenYearsAgo) {
      setErrorMessage(t.invalidDobError);
      setIsLoading(false);
      return;
    }

    // Defensive only — state-check already blocks Puerto Rico earlier in the flow,
    // so this should normally be unreachable.
    if (isPuertoRico(selectedState)) {
      setIsLoading(false);
      return;
    }

    // Validate first name minimum length
    if (form.firstName.trim().length < 2) {
      setErrorMessage(t.firstNameTooShortError);
      setIsLoading(false);
      return;
    }

    // Validate last name minimum length
    if (form.lastName.trim().length < 2) {
      setErrorMessage(t.lastNameTooShortError);
      setIsLoading(false);
      return;
    }

    // Validate email TLD
    if (!isValidEmailTLD(email)) {
      setErrorMessage(t.invalidEmailError);
      setIsLoading(false);
      return;
    }

    // Strip formatting from phone number for backend (send only digits)
    const phoneDigits = form.phone.replace(/\D/g, "");
    const normalizedFirstName = normalizeInput(form.firstName.trim());
    const normalizedLastName = normalizeInput(form.lastName.trim());
    if (phoneDigits.length < 10) {
      setPhoneTouched(true);
      setErrorMessage(t.invalidPhoneError);
      setIsLoading(false);
      return;
    }

    if (!isValidUsNationalPhoneDigits(phoneDigits)) {
      setPhoneTouched(true);
      setErrorMessage(t.invalidPhoneError);
      setIsLoading(false);
      return;
    }

    if (isReservedNanp5550100Through0199(phoneDigits)) {
      setPhoneTouched(true);
      setErrorMessage(t.phoneTestLineError);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const payload = {
      email,
      customer_id: shopifyCustomerId || customerId,
      state: selectedState,
      first_name: normalizedFirstName,
      last_name: normalizedLastName,
      dob: dobString,
      sex: form.sex,
      phone: phoneDigits,
      sms_marketing_accept: form.sms_marketing_accept ? "true" : "false",
      tnc1: form.tnc,
      tnc2: form.tnc,
      locale: language,
    };

    // Get storefront config based on current host (production vs QA)
    const currentHost = window.location.host;
    const { storeFrontAPI } = getStorefrontConfig(currentHost);
    const storeFrontUrl = getStorefrontUrl(currentHost);

    try {
      // Resolve the Shopify account + token this submit will use for the MDI call below. For a
      // brand-new registrant (no token yet) this creates the Shopify account first and AWAITS
      // it — read the result from the resolved value, not from `customerAccessToken`/
      // `shopifyCustomerId` props/state, which won't have re-rendered yet inside this same
      // synchronous submit. This guarantees /apps/rx/new-customer only ever fires with a real,
      // already-confirmed Shopify customer id/token.
      let effectiveToken = customerAccessToken;
      let effectiveShopifyCustomerId = shopifyCustomerId;
      let justRegistered = false;

      if (!effectiveToken) {
        const shopifyResult = await createShopifyAccount({ email, password, storeFrontUrl, storeFrontAPI });

        if (!shopifyResult.success || !shopifyResult.token) {
          if (shopifyResult.errorKind === "password_breach") {
            setErrorMessage(t.passwordBreachError);
          } else if (shopifyResult.errorKind === "login_failed") {
            setErrorMessage(t.checkDetailsError);
          } else {
            setErrorMessage(shopifyResult.message || t.checkDetailsError);
          }
          setIsLoading(false);
          return;
        }

        effectiveToken = shopifyResult.token;
        effectiveShopifyCustomerId = shopifyResult.shopifyCustomerId || null;
        justRegistered = true;

        // Fires "Account Created" exactly once, at the real moment of creation. The Klaviyo
        // list subscribe itself already happened earlier, at email-capture (SRX-258).
        await identifyOnAccountCreation({ email });
        await silentlyLoginViaMultipass(effectiveToken);
      }

      if (effectiveToken) {
        await updateShopifyCustomer({
          customerAccessToken: effectiveToken,
          storeFrontUrl,
          storeFrontAPI,
          firstName: normalizedFirstName,
          lastName: normalizedLastName,
          phone: `+1${phoneDigits}`,
        });
      }

      // --- Always create the MDI customer, now that the Shopify account/token are confirmed
      // Exclude sms_marketing_accept from MDI payload (Klaviyo-only, not an MDI field)
      const { sms_marketing_accept, ...mdiPayload } = payload;
      mdiPayload.customer_id = effectiveShopifyCustomerId || mdiPayload.customer_id;

      const response = await fetch("/apps/rx/new-customer", {
        method: "POST",
        headers: { "Content-Type": "application/json", 'Accept':'application/json' },
        body: JSON.stringify(mdiPayload),
      });

      const data = await response.json();

      if (!response.ok) {
        const { match, detail } = mdiErrorLooksPhoneRelated(data);
        if (match) {
          setPhoneTouched(true);
          setErrorMessage(detail || t.accountCreationError);
        } else {
          setErrorMessage(t.accountCreationError);
        }
        setIsLoading(false);
        return;
      }

      if (data?.patient_id) {
        onContinue({
          customer_id: data?.patient_id,
          shopify_customer_id: mdiPayload.customer_id,
          email: payload.email,
          firstName: payload.first_name,
          lastName: payload.last_name,
          state: selectedState,
          phone: phoneDigits,
          dob: dobString,
          sex: form.sex,
          tnc: form.tnc,
          sms_marketing_accept: form.sms_marketing_accept,
          customerAccessToken: effectiveToken,
          has_shopify_account: true,
          has_mdi_account: true,
          ...(justRegistered ? { justRegistered: true } : {}),
        });
      } else {
        const { match, detail } = mdiErrorLooksPhoneRelated(data);
        if (match) {
          setPhoneTouched(true);
          setErrorMessage(detail || t.accountCreationError);
        } else {
          setErrorMessage(t.accountCreationError);
        }
        setIsLoading(false);
      }
    } catch {
      setErrorMessage(t.accountCreationError);
      setIsLoading(false);
    }
  };

  const isStepValid = (step: AccountDetailsStep) => {
    switch (step) {
      case "identity":
        return (
          form.firstName.trim().length >= 2 &&
          form.lastName.trim().length >= 2 &&
          !isDobInvalid &&
          form.dobMonth.trim() !== "" &&
          form.dobDay.trim() !== "" &&
          form.dobYear.trim().length === 4 &&
          form.sex !== ""
        );
      case "access":
        return (
          isPhoneAndStateComplete(form.phone, selectedState, isPR) &&
          form.tnc &&
          (!needsPassword || (passwordValidation.isValid && password === confirmPassword))
        );
      default:
        return false;
    }
  };

  const handleContinueToAccess = () => {
    if (!isStepValid("identity")) {
      setFirstNameTouched(true);
      setLastNameTouched(true);
      setDobTouched(true);
      setSexTouched(true);
      focusFirstInvalidField([
        form.firstName.trim().length < 2 ? "firstName" : null,
        form.lastName.trim().length < 2 ? "lastName" : null,
        form.dobMonth.trim() === "" ? "dobMonth" : null,
        form.dobDay.trim() === "" ? "dobDay" : null,
        form.dobYear.trim().length !== 4 ? "dobYear" : null,
        form.sex === "" ? "sex-male" : null,
      ]);
      return;
    }
    setErrorMessage("");
    // Arm before paint so a delayed "ghost" click cannot submit the access screen in the same interaction.
    armGuard();
    onAccountDetailsStepChange("access");
  };

  const handleStepBack = () => {
    if (accountDetailsStep === "identity") {
      onBack();
      return;
    }
    onAccountDetailsStepChange("identity");
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter" || accountDetailsStep === "access") return;
    e.preventDefault();
    handleContinueToAccess();
  };

  return (
    <div className="w-full min-h-full flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
      {/* No onBack here — the form already has its own dedicated Back button below,
          and showing both was redundant. */}
      <Header logoUrl={logoUrl} onClose={onClose} />
      <div className="max-w-[640px] flex flex-col h-[calc(100%-40px)] mx-auto px-5 py-[30px] lg:px-4 w-full mb-0">
        <ProgressBar
          currentStep={currentStep}
          totalSteps={totalSteps}
          sectionIndex={sectionIndex}
          totalSections={totalSections}
          sectionId={sectionId}
          language={language}
        />
        <form
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          className="mt-8 pb-[30px]"
        >
      {/* Content */}
      <div className="space-y-6">
        {/* Page Title */}
        <div className="space-y-2.5">
          <h1
            className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {accountDetailsStep === "identity" ? t.stepTitleIdentity : t.stepTitleAccess}
          </h1>
          <p
            className="text-[16px] leading-[22px] text-gray-700"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {accountDetailsStep === "identity" ? t.identitySubtitle : t.accessSubtitle}
          </p>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
          <span
            className="text-[16px] leading-[22px] text-gray-500 shrink-0"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {t.emailLabel}:
          </span>
          <span
            className="text-[16px] leading-[22px] text-gray-700 break-all"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {email}
          </span>
        </div>

      {accountDetailsStep === "identity" && (
      <>
      {/* First Name */}
      <div>
        <label htmlFor="firstName" className="block mb-2 text-sm font-medium required-label">
          {language === "es" ? "Nombre" : "First Name"}
        </label>
        <input
          id="firstName"
          type="text"
          name="firstName"
          autoComplete="given-name"
          value={form.firstName}
          onChange={handleChange}
          onBlur={() => setFirstNameTouched(true)}
          className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[16px] bg-white text-[#353535] placeholder:text-[#676767] ${
            firstNameError ? "ring-[#E34D52]" : "ring-gray-300"
          }`}
          placeholder={language === "es" ? "ej. Juan" : "e.g. John"}
          aria-required="true"
          data-testid="register_input-first_name"
          aria-invalid={!!firstNameError}
        />
        {firstNameError && (
          <div
            className="mt-2 text-[#E34D52] text-sm leading-[21px]"
            style={{ fontFamily: "TT Commons Pro" }}
            role="alert"
          >
            {firstNameError}
          </div>
        )}
      </div>

      {/* Last Name */}
      <div>
        <label htmlFor="lastName" className="block mb-2 text-sm font-medium required-label">
          {language === "es" ? "Apellido" : "Last Name"}
        </label>
        <input
          id="lastName"
          type="text"
          name="lastName"
          autoComplete="family-name"
          value={form.lastName}
          onChange={handleChange}
          onBlur={() => setLastNameTouched(true)}
          className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[16px] bg-white text-[#353535] placeholder:text-[#676767] ${
            lastNameError ? "ring-[#E34D52]" : "ring-gray-300"
          }`}
          placeholder={language === "es" ? "ej. Pérez" : "e.g. Doe"}
          aria-required="true"
          data-testid="register_input-last_name"
          aria-invalid={!!lastNameError}
        />
        {lastNameError && (
          <div
            className="mt-2 text-[#E34D52] text-sm leading-[21px]"
            style={{ fontFamily: "TT Commons Pro" }}
            role="alert"
          >
            {lastNameError}
          </div>
        )}
      </div>

      {/* Date of Birth — always Month→Day→Year (US format), regardless of language */}
      <fieldset>
        <legend className="block mb-2 text-sm font-medium required-label">
          {language === "es" ? "Fecha de nacimiento" : "Date of Birth"}
        </legend>
        <p className="mb-2 text-sm text-gray-500" style={{ fontFamily: "TT Commons Pro" }}>
          {language === "es" ? "Por ejemplo, 03 24 1988" : "For example, 03 24 1988"}
        </p>
        <div className="flex gap-3" role="group" aria-required="true">
          {/* Month */}
          <div className="flex-1">
            <label htmlFor="dobMonth" className="sr-only">
              {language === "es" ? "Mes" : "Month"}
            </label>
            <input
              id="dobMonth"
              type="text"
              inputMode="numeric"
              autoComplete="bday-month"
              name="dobMonth"
              value={form.dobMonth}
              onChange={handleDobChange}
              onFocus={(e) => e.target.select()}
              maxLength={2}
              placeholder="MM"
              className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[#353535] placeholder:text-[#676767] text-center ${
                isDobInvalid || showDobRequiredError ? 'ring-[#E34D52]' : 'ring-gray-300'
              }`}
              aria-label={language === "es" ? "Mes de nacimiento" : "Birth month"}
              aria-invalid={isDobInvalid || showDobRequiredError}
              data-testid="register_input-dob_month"
            />
          </div>
          {/* Day */}
          <div className="flex-1">
            <label htmlFor="dobDay" className="sr-only">
              {language === "es" ? "Día" : "Day"}
            </label>
            <input
              id="dobDay"
              type="text"
              inputMode="numeric"
              autoComplete="bday-day"
              name="dobDay"
              value={form.dobDay}
              onChange={handleDobChange}
              onFocus={(e) => e.target.select()}
              maxLength={2}
              placeholder="DD"
              className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[#353535] placeholder:text-[#676767] text-center ${
                isDobInvalid || showDobRequiredError ? 'ring-[#E34D52]' : 'ring-gray-300'
              }`}
              aria-label={language === "es" ? "Día de nacimiento" : "Birth day"}
              aria-invalid={isDobInvalid || showDobRequiredError}
              data-testid="register_input-dob_day"
            />
          </div>
          {/* Year */}
          <div className="flex-[1.5]">
            <label htmlFor="dobYear" className="sr-only">
              {language === "es" ? "Año" : "Year"}
            </label>
            <input
              id="dobYear"
              type="text"
              inputMode="numeric"
              autoComplete="bday-year"
              name="dobYear"
              value={form.dobYear}
              onChange={handleDobChange}
              onFocus={(e) => e.target.select()}
              maxLength={4}
              placeholder={language === "es" ? "AAAA" : "YYYY"}
              className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[#353535] placeholder:text-[#676767] text-center ${
                isDobInvalid || showDobRequiredError ? 'ring-[#E34D52]' : 'ring-gray-300'
              }`}
              aria-label={language === "es" ? "Año de nacimiento" : "Birth year"}
              aria-invalid={isDobInvalid || showDobRequiredError}
              data-testid="register_input-dob_year"
            />
          </div>
        </div>
        {isDobInvalid && (
          <div
            id="dob-error"
            className="mt-2 rounded text-[#E34D52] text-sm leading-[21px]"
            style={{ fontFamily: 'TT Commons Pro' }}
            role="alert"
            aria-live="assertive"
          >
            {language === "es"
              ? "Debe tener al menos 18 años."
              : "You must be at least 18 years old."}
          </div>
        )}
        {showDobRequiredError && (
          <div
            id="dob-required-error"
            className="mt-2 rounded text-[#E34D52] text-sm leading-[21px]"
            style={{ fontFamily: 'TT Commons Pro' }}
            role="alert"
            aria-live="assertive"
          >
            {t.dobRequiredError || t.fieldRequired}
          </div>
        )}
      </fieldset>

      {/* Sex (radio buttons) */}
      <fieldset>
        <legend className="block mb-2 text-sm font-medium required-label">
          {language === "es" ? "Sexo" : "Sex"}
        </legend>
        <div className="flex gap-3" role="group" aria-required="true">
          <label
            className={`flex items-center gap-4 flex-1 bg-white rounded-md px-4 py-4 cursor-pointer transition-all ${
              form.sex === "male"
                ? "ring-2 ring-black"
                : showSexRequiredError
                  ? "ring-1 ring-[#E34D52]"
                  : "ring-1 ring-gray-300"
            }`}
          >
            <div className="relative flex items-center justify-center w-4 h-4">
              {/* Outer circle */}
              <div
                className={`absolute w-4 h-4 rounded-full ${
                  form.sex === "male" ? "bg-black" : "bg-[#E0E0E0]"
                }`}
              />
              {/* White gap */}
              <div className="absolute w-3.5 h-3.5 rounded-full bg-white" />
              {/* Inner circle */}
              <div
                className={`absolute w-3 h-3 rounded-full ${
                  form.sex === "male" ? "bg-black" : "bg-[#E0E0E0]"
                }`}
                style={{ width: "12px", height: "12px" }}
              />
              <input
                id="sex-male"
                type="radio"
                name="sex"
                value="male"
                autoComplete="sex"
                checked={form.sex === "male"}
                onChange={(e) => {
                  setSexTouched(true);
                  handleChange(e);
                }}
                className="sr-only"
                data-testid="register_radio-sex_male"
              />
            </div>
            <span
              style={{ fontFamily: "TT Commons Pro", lineHeight: "24px" }}
              className="text-base font-normal text-black"
            >
              {language === "es" ? "Hombre" : "Male"}
            </span>
          </label>

          <label
            className={`flex items-center gap-4 flex-1 bg-white rounded-md px-4 py-4 cursor-pointer transition-all ${
              form.sex === "female"
                ? "ring-2 ring-black"
                : showSexRequiredError
                  ? "ring-1 ring-[#E34D52]"
                  : "ring-1 ring-gray-300"
            }`}
          >
            <div className="relative flex items-center justify-center w-4 h-4">
              <div
                className={`absolute w-4 h-4 rounded-full ${
                  form.sex === "female" ? "bg-black" : "bg-[#E0E0E0]"
                }`}
              />
              <div className="absolute w-3.5 h-3.5 rounded-full bg-white" />
              <div
                className={`absolute w-3 h-3 rounded-full ${
                  form.sex === "female" ? "bg-black" : "bg-[#E0E0E0]"
                }`}
                style={{ width: "12px", height: "12px" }}
              />
              <input
                type="radio"
                name="sex"
                value="female"
                autoComplete="sex"
                checked={form.sex === "female"}
                onChange={(e) => {
                  setSexTouched(true);
                  handleChange(e);
                }}
                className="sr-only"
                data-testid="register_radio-sex_female"
              />
            </div>
            <span
              style={{ fontFamily: "TT Commons Pro", lineHeight: "24px" }}
              className="text-base font-normal text-black"
            >
              {language === "es" ? "Mujer" : "Female"}
            </span>
          </label>
        </div>
        {showSexRequiredError && (
          <div
            className="mt-2 text-[#E34D52] text-sm leading-[21px]"
            style={{ fontFamily: "TT Commons Pro" }}
            role="alert"
          >
            {t.sexRequiredError || t.fieldRequired}
          </div>
        )}
      </fieldset>
      </>
      )}

      {accountDetailsStep === "access" && (
      <>
      {/* Phone Number */}
      <div>
        <label htmlFor="phone" className="block mb-2 text-sm font-medium required-label">
          {language === "es" ? "Número de teléfono" : "Phone Number"}
        </label>
        <input
          id="phone"
          type="tel"
          name="phone"
          autoComplete="tel"
          inputMode="tel"
          value={form.phone}
          onChange={handleChange}
          onBlur={() => setPhoneTouched(true)}
          className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[#353535] placeholder:text-[#676767] ${
            isPhoneFieldInvalidForStyle ? "ring-[#E34D52]" : "ring-gray-300"
          }`}
          placeholder={language === "es" ? "(555) 555-5555" : "(555) 555-5555"}
          aria-required="true"
          data-testid="register_input-phone"
          aria-invalid={isPhoneFieldInvalidForStyle}
        />
        {showPhoneUnderFieldError ? (
          <div
            className="mt-2 text-[#E34D52] text-sm leading-[21px]"
            style={{ fontFamily: "TT Commons Pro" }}
            role="alert"
          >
            {phoneUnderFieldMessage}
          </div>
        ) : (
          <p
            className="mt-2 text-sm text-gray-500"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {t.phoneReassurance}
          </p>
        )}
      </div>

      {/* Terms & Conditions is captured earlier at email-capture (SRX-202) for every path that
          can reach this screen, so no checkbox is collected here. */}
      <div className="space-y-3 text-sm">
        {/* SMS Marketing Checkbox */}
        <div className="flex items-start gap-3">
          <input
            id="sms_marketing_accept"
            type="checkbox"
            name="sms_marketing_accept"
            checked={form.sms_marketing_accept}
            onChange={handleChange}
            className="mt-1 h-6 w-6 flex-shrink-0 rounded border-gray-300 accent-[#2D55FB]"
            data-testid="register_checkbox-sms"
          />
          <div>
            <label htmlFor="sms_marketing_accept" className="text-sm font-medium">
              {tShipping?.smsOptinLabel}
            </label>
            <p className="text-sm text-gray-600 mt-1">
              {tShipping?.smsOptinDescription?.split("View Privacy Policy & Terms")[0]}
              {" "}
              <a href="/policies/privacy-policy" className="tnc-link" target="_blank" rel="noopener noreferrer">
                {tShipping?.privacyPolicyLink}
              </a>
              {" "}
              {tShipping?.privacyAndTermsConnector}
              {" "}
              <a href="/policies/terms-of-service" className="tnc-link" target="_blank" rel="noopener noreferrer">
                {tShipping?.termsAndConditionsLink}
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Password — SRX-198: collected here alongside phone. Only shown for a brand-new
          registrant; a customer who arrived via AccountLogin already has a Shopify account. */}
      {needsPassword && (
        <>
          <div>
            <label htmlFor="password" className="block mb-2 text-sm font-medium required-label">
              {t.passwordLabel}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPasswordText ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPasswordTouched(true);
                  setPassword(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[#353535] placeholder:text-[#676767] pr-12 ${
                  showPasswordRequiredError ? "ring-[#E34D52]" : "ring-gray-300"
                }`}
                placeholder={t.passwordPlaceholder || ""}
                aria-required="true"
                aria-invalid={showPasswordRequiredError}
                data-testid="register_input-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswordText(!showPasswordText)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={
                  showPasswordText
                    ? language === "es" ? "Ocultar contraseña" : "Hide password"
                    : language === "es" ? "Mostrar contraseña" : "Show password"
                }
                data-testid="register_btn-toggle_password"
              >
                {showPasswordText ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {showPasswordRequiredError && (
              <p className="mt-2 text-sm text-[#E34D52]" role="alert">
                {t.passwordRequiredError || t.fieldRequired}
              </p>
            )}
            <PasswordChecklist password={password} language={language} />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium required-label">
              {t.confirmPasswordLabel || "Confirm Password"}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPasswordText ? "text" : "password"}
                name="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                onBlur={() => setConfirmPasswordTouched(true)}
                className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white text-[#353535] placeholder:text-[#676767] pr-12 ${
                  showConfirmPasswordError ? "ring-[#E34D52]" : "ring-gray-300"
                }`}
                aria-required="true"
                aria-invalid={showConfirmPasswordError}
                data-testid="register_input-confirm_password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPasswordText(!showConfirmPasswordText)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={
                  showConfirmPasswordText
                    ? language === "es" ? "Ocultar contraseña" : "Hide password"
                    : language === "es" ? "Mostrar contraseña" : "Show password"
                }
                data-testid="register_btn-toggle_confirm_password"
              >
                {showConfirmPasswordText ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {showConfirmPasswordError && (
              <p className="mt-2 text-sm text-[#E34D52]" role="alert">
                {confirmPasswordErrorMessage}
              </p>
            )}
          </div>
        </>
      )}
      </>
      )}

        {/* Error Message */}
        {errorMessage && (
          <div
            className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
            role="alert"
            aria-live="assertive"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {errorMessage}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-6">
          <div className="space-y-3">
            <Button
              type="button"
              onClick={handleStepBack}
              className="w-full h-[50px] bg-transparent hover:bg-gray-100 text-gray-600 rounded-full flex items-center justify-center transition-colors border border-gray-300"
              data-testid="register_btn-back"
            >
              {language === "es" ? "Atrás" : "Back"}
            </Button>
            {accountDetailsStep === "identity" ? (
              <Button
                type="button"
                onClick={handleContinueToAccess}
                data-testid="register_btn-continue_step"
                className="w-full h-[50px] text-white text-lg leading-6 font-medium rounded-full flex items-center justify-center transition-colors bg-[#2D55FB] hover:bg-[#2347E8]"
              >
                {t.continue}
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isLoading}
                aria-busy={isLoading}
                data-testid="register-continue"
                className={`w-full h-[50px] text-white text-lg leading-6 font-medium rounded-full flex items-center justify-center transition-colors bg-[#2D55FB] ${
                  isLoading ? "cursor-not-allowed" : "hover:bg-[#2347E8]"
                }`}
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    role="status"
                    aria-label="Loading"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                ) : (
                  t.continue
                )}
              </Button>
            )}
          </div>
          <ReassuranceLine text={t.dataProtectedReassurance} className="mt-3" />
        </div>
      </div>
        </form>
      </div>
    </div>
  );
}
